import { cva, type VariantProps } from 'class-variance-authority';

/**
 * Shared container / layout variants for the DS card family (DeckCard,
 * FolderCard, StudyGroupCard) — all mirror the Figma "Cards Updated" frame
 * (`YZK9r3wDhvw1pY60KlVEIj`, node `3153:44267`). Grid = 180×180 tile, list =
 * full-width row. No hover / pressed / focused states (display surface;
 * press affordance comes from the app).
 *
 * Axes:
 *   view:  grid | list — Figma's "View" property.
 *   media: cover present — derived from the `image` prop by each card, not
 *          a public prop.
 *
 * The border is NOT on the container — it's a `cardBorderClass` overlay on
 * top, so the absolutely-positioned body panel's `inset-x-0` spans the FULL
 * card width (border included) and its rounded top corners align exactly
 * with the border (no corner discontinuity). Shadows are web-only tokens
 * (`web:shadow-card*`); native gets `CARD_NATIVE_SHADOW`.
 */
/**
 * Card surface alpha per view — an INLINE style, deliberately not a class.
 *
 * These used to live in the `view` variants below as `bg-secondary-50/80` and
 * `bg-secondary-50/70`. An alpha utility is runtime-processed by NativeWind,
 * and the `view` axis is swapped on a LIVE element every time a screen's
 * grid/list toggle flips — which is the bogus "Couldn't find a navigation
 * context" Render Error, at its worst mid-relayout, which is exactly when a
 * view toggle fires. It shipped: the toggle crashed, and the aborted render
 * left list rows wearing the grid container's styling.
 *
 * Keeping the alpha out here means flipping `view` swaps only static classes,
 * so no consumer has to remember to remount. `cardVariants.spec.ts` fails if a
 * runtime-processed class comes back to this axis. See `restyleKey`'s docs for
 * the two sanctioned fixes; this is the first one.
 */
export const CARD_SURFACE_ALPHA = { grid: 0.8, list: 0.7 } as const;

export const cardVariants = cva('relative flex rounded-[28px]', {
  variants: {
    view: {
      grid: 'flex-col overflow-hidden p-2 web:shadow-card',
      // min-h = the media-row geometry (p-[4px] + 78px thumbnail + py-[8px]
      // + 1px borders), so no-media rows keep the same height instead of
      // collapsing to their text content. Width is flexible up to 560 —
      // wider than the Figma 360 design width so tablet multi-column list
      // layouts fill their cells; 360 stays the screens' column-fit minimum.
      list: 'min-h-[102px] w-full max-w-[560px] flex-row items-center gap-[12px] web:shadow-card-row',
    },
    // Grid subvariant. `compact` is the Figma 180 square (the mobile tile);
    // `expanded` is the web dashboard tile — the compact geometry scaled
    // 1.25× (180→225), so everything inside scales uniformly. Ignored by
    // list. Like the folder card's `max-w-full`, expanded still shrinks on
    // narrow columns.
    size: { compact: '', expanded: '' },
    media: { true: '', false: '' },
  },
  compoundVariants: [
    // w-full: the compact tile FILLS its cell — the host grid is responsible
    // for sizing that cell so the fill width lands at a sensible card width
    // (mobile solves it with a size band per layout class; Figma `180:18816`
    // draws the stretched ~191.5 card, not a fixed 180). The HEIGHT stays the
    // fixed 180 at any width. Call sites that need the 180 DESIGN box — web
    // grids, previews, any shrink-to-fit wrapper where a percentage width
    // collapses — state it via `className` (merged after these classes, so it
    // wins); web has a `compactCardClass` helper for exactly that.
    //
    // The band's numbers deliberately do NOT appear here: they are the app's
    // tunable constants (and provisional, pending designer review), so
    // restating them in the DS would leave this file documenting a geometry
    // the consumer no longer runs.
    { view: 'grid', size: 'compact', class: 'h-[180px] w-full' },
    // Expanded box W/H come from an inline style ×responsive-scale (DeckCard).
    { view: 'grid', size: 'expanded', class: 'max-w-full' },
    // List rows share ONE trailing rule (Figma 5714:29707 / 5714:29197):
    // the ⋯ sits 12px off the right border with or without the thumbnail,
    // and the thumbnail lands 8px left of the ⋯ (its 4px frame − the tuck +
    // the row's 12px gap). Only the vertical padding differs: the media row
    // tightens to 8px around the framed thumbnail. ARBITRARY-PX on purpose,
    // not `py-2 pl-5 pr-3`: rem-based utilities scale ×1.25 on tablets
    // (`useResponsiveRem`) while the row's height and thumbnails are fixed
    // px — the scaled paddings broke the 102 = 8+4+78+4+8 equality and the
    // preview fan overflowed the card's edge on iPad. The DS card family
    // deliberately does NOT grow on tablets (iPad fits MORE cards).
    { view: 'list', media: true, class: 'py-[8px] pl-[20px] pr-[12px]' },
    { view: 'list', media: false, class: 'py-[16px] pl-[20px] pr-[12px]' },
  ],
  defaultVariants: { view: 'grid', size: 'compact', media: false },
});

/**
 * Grid body panel position — the scooped sheet overlapping the tile's bottom,
 * full card width (Figma "Card Body" 89px compact; expanded = 110, node
 * 5656:17006, matching the expanded panel path's viewBox so the shape isn't
 * stretched). The panel's SURFACE is drawn by {@link CardBodyPanel}; these
 * classes are layout only — a pure POSITION box on both sizes: the rows
 * inside place themselves absolutely at the Figma offsets (see DeckCard and
 * FolderLikeCard's compact panel, which has always worked this way).
 *
 * It used to lay the compact rows out in FLOW (`justify-end gap-1 px-4 pb-4`),
 * which made the title's top gap whatever slack was left over — and on a
 * tablet there is none. The panel's 89px is fixed px while `gap-1`/`pb-4`,
 * the 24px chip row and the 32px ⋯ button are all rem, so the tablet ramp
 * (×1.25, see `useResponsiveRem`) inflated the content to ~95px inside an
 * 89px box: the title was pushed clean off the panel's top edge and sat
 * against the cover with no gap at all. Absolute rows state the design's
 * offsets instead of deriving them, so the anatomy holds at every scale.
 */
export const cardBodyVariants = cva('absolute inset-x-0 bottom-0', {
  // Expanded height comes from an inline style ×responsive-scale (DeckCard).
  variants: { size: { compact: 'h-[89px]', expanded: '' } },
  defaultVariants: { size: 'compact' },
});

/**
 * The compact grid panel's anatomy (Figma `171:17544` — deck — and
 * `171:17564` — folder), in ARBITRARY PX for the reason the list row states
 * its paddings the same way: the panel's height is fixed px, so anything
 * positioned inside it must be too, or the tablet rem ramp walks the rows
 * out of a box that cannot grow with them.
 *
 * Deck and folder differ only in where their panel's top edge lands (the
 * folder's scooped shoulder sits 4px lower), so the title row is per-shape
 * while everything else is one rule.
 */
export const COMPACT_PANEL = {
  /** Left inset of the title and chip rows. */
  left: 16,
  /** Right inset of the ⋯ button (and of the chip row on the deck card). */
  right: 16,
  /**
   * Where the title row starts, per shape: deck `171:17555` y=14.5,
   * folder `171:17578` y=18 (its panel's shoulder is lower). Both rows are
   * 32 tall — the ⋯ button's box, with the 24px title line centred in it.
   */
  titleTop: { deck: 14.5, folder: 18 },
  titleRowHeight: 32,
  /**
   * Gap between the title row and the ⋯ — the title box ends here, so its
   * clamp (and its fade) begins 8px before the button.
   */
  titleGap: 8,
  /** Chip row's offset from the panel's bottom edge — the DS 16px rule. */
  chipsBottom: 16,
} as const;

/**
 * Holds the compact tile's title at its DESIGN type (H4, 16/24) whatever the
 * rem ramp is doing — the last piece of the card that still grew on a tablet
 * once the paddings and chips were pinned.
 *
 * `native:`-only: the ramp is a native-only mechanism (`ResponsiveScaleProvider`
 * swaps NativeWind's `rem`), while on web `text-h4` is 1rem and MUST stay that
 * way so browser zoom and a user's default font size still reach it.
 *
 * Why pin at all, when the rest of a tablet's UI scales: this tile is a fixed
 * 180×180 box. Type that grows inside a box that can't is how the title ended
 * up flush against the panel edge, and it costs ~20% of the characters the
 * title can show — on the one card whose whole job is naming things. It also
 * matches the platforms: iOS and Android scale text with the USER's text-size
 * setting, not with screen size, and a 16pt label is 16pt on a phone and on a
 * tablet. The DS already states the intent for this family — an iPad fits MORE
 * cards, not bigger ones (see `cardVariants`' compact note).
 *
 * The EXPANDED tile is deliberately excluded: it scales its whole box by
 * `useResponsiveScale`, so its title must ramp with it or the card stops being
 * proportionally the web card.
 */
export const COMPACT_TITLE_PIN = 'native:text-[16px] native:leading-[24px]';

/** Title row's right inset: clears the ⋯ button and the 8px gap before it. */
export const COMPACT_TITLE_RIGHT =
  COMPACT_PANEL.right + COMPACT_PANEL.titleRowHeight + COMPACT_PANEL.titleGap;

/**
 * The compact panel's three rows as ready-made style objects, so both cards
 * lay their panel out from ONE source. `shape` only moves the title row's
 * top edge (see {@link COMPACT_PANEL.titleTop}); the folder card additionally
 * runs its chip row 12px off the right, where its one-line clip already sat.
 *
 * There are exactly TWO possible results, so they are built once and shared
 * rather than rebuilt per call. React Native prop-diffs `style` BY REFERENCE:
 * a fresh literal per render re-sent three positioned rows of every visible
 * card to native on any re-render, and every card on screen calls this.
 */
function buildCompactPanelRows(shape: 'deck' | 'folder') {
  return {
    title: {
      left: COMPACT_PANEL.left,
      right: COMPACT_TITLE_RIGHT,
      top: COMPACT_PANEL.titleTop[shape],
      height: COMPACT_PANEL.titleRowHeight,
      gap: COMPACT_PANEL.titleGap,
    },
    chips: {
      left: COMPACT_PANEL.left,
      right: shape === 'folder' ? 12 : COMPACT_PANEL.right,
      bottom: COMPACT_PANEL.chipsBottom,
    },
    actions: {
      right: shape === 'folder' ? 12 : COMPACT_PANEL.right,
      top: COMPACT_PANEL.titleTop[shape],
      height: COMPACT_PANEL.titleRowHeight,
    },
  } as const;
}

const COMPACT_PANEL_ROWS = {
  deck: buildCompactPanelRows('deck'),
  folder: buildCompactPanelRows('folder'),
} as const;

export function compactPanelRows(shape: 'deck' | 'folder') {
  return COMPACT_PANEL_ROWS[shape];
}

/** @deprecated compact-only alias — use {@link cardBodyVariants}. */
export const cardBodyClass = cardBodyVariants({ size: 'compact' });

/**
 * The card's 1px border, drawn as a top-most overlay (not on the container)
 * so the body panel can span the full border-box width and align to it.
 */
export const cardBorderClass =
  'pointer-events-none absolute inset-0 rounded-[28px] border border-secondary-200';

/**
 * Title typography per view: grid = H4 (16/24), list = H3 (18/26), both Mona
 * Sans SemiBold. List titles read near-black (`secondary-950`), grid titles
 * the softer `secondary-800` — per the Figma nodes.
 */
export const cardTitleVariants = cva(
  'min-w-0 flex-1 font-semibold native:font-mona-semibold',
  {
    variants: {
      view: {
        grid: 'text-h4 text-secondary-800',
        list: 'text-h3 text-secondary-950',
      },
    },
    defaultVariants: { view: 'grid' },
  },
);

export type CardVariantProps = VariantProps<typeof cardVariants>;
